/**
 * HotelDetailPage — industry-standard hotel detail layout.
 *
 * Sections:
 *   1. Sticky top bar: hotel name + rating + Save/Share
 *   2. Gallery hero (5-image collage + "View all photos" lightbox)
 *   3. Title bar: name, star rating, property-type chip, full address
 *      with map pin + scroll-to-rooms CTA on the right
 *   4. Quick-facts strip: check-in time, check-out time, free cancellation
 *      coverage, free WiFi/breakfast highlights
 *   5. About this property (parsed `description` blob with subsections)
 *   6. Amenities grid (deduped, icon-mapped)
 *   7. Map placeholder (lat/lng) with Google Maps deep-link
 *   8. Rooms section: rich option cards with meal-basis, refundability,
 *      free-cancellation date, occupancy, total + per-night, PAN/Passport
 *      flags, Reserve CTA
 *   9. Policies (check-in/out, PAN/passport)
 *  10. Mobile sticky bottom CTA showing starting price + Reserve
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Star, MapPin, Loader2, BadgeCheck, ShieldCheck, Building2,
  Share2, Heart, Clock, Users, Coffee, Wifi, Snowflake, Utensils, Car,
  Dumbbell, Waves, Tv, Briefcase, ParkingCircle, Cigarette, Sparkles,
  Camera, Info, AlertTriangle, ChevronRight, X, Calendar, LogOut, LogIn,
  User as UserIcon, Wallet, ChevronDown, ChevronUp, Filter,
} from 'lucide-react';
import Footer from '../components/Footer';
import BottomNav from '../components/BottomNav';
import { useAgentAuth } from '../context/AgentAuthContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Helpers ──────────────────────────────────────────────────────────
const _StarRow = ({ rating, size = 4 }) => {
  if (!rating) return null;
  const r = Math.round(rating);
  return (
    <div className="flex items-center gap-0.5" data-testid="hotel-detail-stars">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`fill-amber-400 text-amber-400 ${i < r ? 'opacity-100' : 'opacity-25'}`}
          style={{ width: `${size * 4}px`, height: `${size * 4}px` }}
        />
      ))}
    </div>
  );
};

const _propertyTypeLabel = (pt) => {
  if (!pt) return null;
  const t = String(pt).toUpperCase();
  return {
    HOTEL: 'Hotel', HOSTEL: 'Hostel', RESORT: 'Resort',
    APARTMENT: 'Apartment', VILLA: 'Villa', GUESTHOUSE: 'Guest House',
  }[t] || t.charAt(0) + t.slice(1).toLowerCase();
};

// Map amenity strings → icons. Falls back to a generic Sparkles icon.
const AMENITY_MAP = [
  { match: /wi-?fi|internet/i, Icon: Wifi, label: 'WiFi' },
  { match: /breakfast|tea|coffee/i, Icon: Coffee, label: 'Breakfast' },
  { match: /parking|valet/i, Icon: ParkingCircle, label: 'Parking' },
  { match: /car|shuttle|airport.transfer/i, Icon: Car, label: 'Transport' },
  { match: /a\/?c|air.?cond/i, Icon: Snowflake, label: 'AC' },
  { match: /restaurant|dining|food/i, Icon: Utensils, label: 'Restaurant' },
  { match: /gym|fitness/i, Icon: Dumbbell, label: 'Fitness' },
  { match: /pool|swim/i, Icon: Waves, label: 'Pool' },
  { match: /tv|television/i, Icon: Tv, label: 'TV' },
  { match: /business|meeting|conference/i, Icon: Briefcase, label: 'Business' },
  { match: /smoking|smoker/i, Icon: Cigarette, label: 'Smoking' },
  { match: /reception|check.?in|desk|concierge|luggage/i, Icon: Clock, label: '24h Desk' },
];

const _amenityIcon = (name) => {
  const s = String(name || '');
  for (const { match, Icon, label } of AMENITY_MAP) {
    if (match.test(s)) return { Icon, group: label };
  }
  return { Icon: Sparkles, group: 'Service' };
};

// Address one-liner
const _fmtAddress = (ad) => {
  if (!ad) return '';
  const parts = [ad.adr, ad.adr2, ad.city?.name, ad.state?.name, ad.postalCode, ad.country?.name]
    .filter(Boolean).map((s) => String(s).trim()).filter(Boolean);
  return Array.from(new Set(parts)).join(', ');
};

const _fmtCheckTime = (t, prefix) => {
  if (!t) return null;
  if (typeof t === 'string') return `${prefix} ${t}`;
  const time = t.beginTime || t.endTime || t.time;
  if (!time) return null;
  return `${prefix} ${time}${t.minAge ? ` · min age ${Math.round(t.minAge)}` : ''}`;
};

const _fmtDateBadge = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

// ─── Lightbox ─────────────────────────────────────────────────────────
const Lightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex || 0);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % images.length);
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [images.length, onClose]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" data-testid="hotel-detail-lightbox" onClick={onClose}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white"
        data-testid="hotel-detail-lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>
      <img src={images[idx]} alt="" className="max-h-[88vh] max-w-[92vw] object-contain shadow-2xl rounded-xl" onClick={(e) => e.stopPropagation()} />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 text-white text-xs font-bold bg-black/40 px-3 py-1.5 rounded-full">
        {idx + 1} / {images.length}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white"
        aria-label="Previous"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white"
        aria-label="Next"
      >
        <ArrowLeft className="w-5 h-5 rotate-180" />
      </button>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────
const HotelDetailPage = () => {
  const { hotelId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('sessionId');

  const { agent, isAuthenticated: isAgent, logout: agentLogout } = useAgentAuth();
  const { isAuthenticated: isB2C, user: b2cUser, logout: b2cLogout } = useAuth();

  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewingOpt, setReviewingOpt] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [saved, setSaved] = useState(false);
  const roomsRef = useRef(null);

  // Phase H5 — number of rooms requested in the original search. Tripjack's
  // /hotelDetail-search returns options where `ris.length === searched_rooms`,
  // so any option whose grouped room count drifts is filtered out defensively.
  const searchedRoomCount = useMemo(() => {
    const raw = params.get('rooms');
    if (!raw) return 1;
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      if (Array.isArray(parsed) && parsed.length) return parsed.length;
    } catch { /* fall through */ }
    return 1;
  }, [params]);

  // Phase H6 — Filter chips + accordion grouping + Your-Selection rail.
  // `filters` is a small flag bag (default: none active).
  // `expandedGroups` tracks which Room-Type buckets are open; the cheapest
  // bucket auto-opens on first render so the user always sees something.
  // `selectedOptId` drives the "Your Selection" sticky rail.
  const [filters, setFilters] = useState({ refundable: false, breakfast: false, noPan: false, noPassport: false });
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [selectedOptId, setSelectedOptId] = useState(null);
  const groupsAutoOpened = useRef(false);

  useEffect(() => {
    if (!hotelId) return;
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/hotels/${encodeURIComponent(hotelId)}/details${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`,
          { timeout: 30000 },
        );
        if (res.data?.success) setHotel(res.data.hotel);
        else setError('Could not load hotel details.');
      } catch (e) {
        setError(e.response?.data?.detail?.message || e.response?.data?.message || 'Network error.');
      } finally {
        setLoading(false);
      }
    })();
  }, [hotelId, sessionId]);

  const handleBookOption = async (opt) => {
    setReviewingOpt(opt.id);
    setError('');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/hotels/review`,
        { hotelId, optionId: opt.id },
        { timeout: 45000 },
      );
      const body = res.data || {};
      if (!body.success) { setError('Could not validate this room. Please retry.'); return; }
      if (body.soldout)   { setError('This room just sold out. Please pick another option.'); return; }
      const qs = new URLSearchParams({
        hotelId, optionId: opt.id,
        bookingId: body.bookingId || '',
        totalAmount: String(body.totalAmount ?? opt.total_price ?? opt.tpc ?? 0),
        ...(sessionId ? { sessionId } : {}),
        ...(params.get('rooms') ? { rooms: params.get('rooms') } : {}),
        ...(params.get('checkin') ? { checkin: params.get('checkin') } : {}),
        ...(params.get('checkout') ? { checkout: params.get('checkout') } : {}),
        ...(params.get('cityname') ? { cityname: params.get('cityname') } : {}),
        ...(body.fare_changed ? {
          fareChanged: '1',
          oldFare: String(body.fare_alert?.oldFare ?? ''),
          newFare: String(body.fare_alert?.newFare ?? ''),
        } : {}),
      });
      navigate(`/hotels/book?${qs.toString()}`);
    } catch (e) {
      setError(e.response?.data?.detail?.message || 'Could not start booking. Please retry.');
    } finally {
      setReviewingOpt(null);
    }
  };

  // Aggregate amenities for the grid (dedup + group + icon)
  const amenityRows = useMemo(() => {
    const list = (hotel?.amenities || []).map((a) => (typeof a === 'string' ? a : a?.name || ''));
    const seen = new Map();
    for (const name of list) {
      if (!name) continue;
      if (!seen.has(name)) seen.set(name, _amenityIcon(name));
    }
    return Array.from(seen.entries()).map(([name, meta]) => ({ name, ...meta }));
  }, [hotel?.amenities]);

  // Phase H5 — defensive room-count guard. Tripjack groups options to
  // exactly match the searched roomInfo.length, so any drift means the
  // supplier (or a stale cached session) returned a wrong-shape option.
  // Drop silently rather than letting the user book the wrong composition.
  const safeOptions = useMemo(() => {
    const opts = hotel?.options || [];
    if (!searchedRoomCount || searchedRoomCount <= 0) return opts;
    return opts.filter((o) => {
      const ris = Array.isArray(o?.rooms_in_option) ? o.rooms_in_option : null;
      // If `rooms_in_option` is missing we can't verify — let it pass.
      if (!ris) return true;
      return ris.length === searchedRoomCount;
    });
  }, [hotel?.options, searchedRoomCount]);

  // Phase H6 — Apply filter chips on top of safeOptions.
  const _mealHasBreakfast = (mb) => {
    if (!mb) return false;
    const s = Array.isArray(mb) ? mb.join(' ') : String(mb);
    return /breakfast|bb|b&b/i.test(s);
  };

  const filteredOptions = useMemo(() => {
    return safeOptions.filter((o) => {
      if (filters.refundable && !o.is_refundable) return false;
      if (filters.breakfast && !_mealHasBreakfast(o.meal_basis || o.mb)) return false;
      if (filters.noPan && o.pan_required) return false;
      if (filters.noPassport && o.passport_required) return false;
      return true;
    });
  }, [safeOptions, filters]);

  // Phase H6 — Group options by `room_type_group_key` (supplied by backend
  // projection). Each group ships with: label, options[], from_price (min
  // total_price across options in the group), refundable-any, breakfast-any.
  const groups = useMemo(() => {
    const map = new Map();
    for (const opt of filteredOptions) {
      const key = opt.room_type_group_key || 'standard';
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: opt.room_type_group_label || opt.name || 'Standard Room',
          options: [],
          from_price: Infinity,
          any_refundable: false,
          any_breakfast: false,
        });
      }
      const g = map.get(key);
      g.options.push(opt);
      const p = Number(opt.total_price ?? opt.tpc ?? Infinity);
      if (p < g.from_price) g.from_price = p;
      if (opt.is_refundable) g.any_refundable = true;
      if (_mealHasBreakfast(opt.meal_basis || opt.mb)) g.any_breakfast = true;
    }
    // Sort groups by cheapest from_price ascending so user sees best deal first
    return Array.from(map.values()).sort((a, b) => a.from_price - b.from_price);
  }, [filteredOptions]);

  // Auto-open the cheapest group once per hotel load (so user always sees
  // at least one accordion expanded). User can collapse if they want.
  useEffect(() => {
    if (!groupsAutoOpened.current && groups.length > 0) {
      setExpandedGroups(new Set([groups[0].key]));
      groupsAutoOpened.current = true;
    }
  }, [groups]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleFilter = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));
  const clearFilters = () => setFilters({ refundable: false, breakfast: false, noPan: false, noPassport: false });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // Counts for chip badges — show only options that would survive
  // each chip applied on its own (helps user predict).
  const chipCounts = useMemo(() => {
    return {
      refundable: safeOptions.filter((o) => o.is_refundable).length,
      breakfast: safeOptions.filter((o) => _mealHasBreakfast(o.meal_basis || o.mb)).length,
      noPan: safeOptions.filter((o) => !o.pan_required).length,
      noPassport: safeOptions.filter((o) => !o.passport_required).length,
    };
  }, [safeOptions]);

  // The currently selected option (drives the right-rail "Your Selection")
  const selectedOption = useMemo(
    () => (selectedOptId ? safeOptions.find((o) => o.id === selectedOptId) : null),
    [selectedOptId, safeOptions],
  );

  const handleSelectOption = (opt) => {
    setSelectedOptId(opt.id);
    // Open the group of the selected option so user sees context
    if (opt.room_type_group_key) {
      setExpandedGroups((prev) => new Set([...prev, opt.room_type_group_key]));
    }
  };

  const startingPrice = useMemo(() => {
    const prices = safeOptions.map((o) => Number(o.total_price ?? o.tpc ?? 0)).filter((p) => p > 0);
    return prices.length ? Math.min(...prices) : null;
  }, [safeOptions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 py-6 space-y-3 animate-pulse">
          <div className="h-10 w-40 bg-white border border-gray-200 rounded-xl" />
          <div className="grid grid-cols-2 gap-2 h-72">
            <div className="bg-gray-200 rounded-2xl" />
            <div className="bg-gray-200 rounded-2xl" />
          </div>
          {Array.from({ length: 3 }, (_, i) => <div key={i} className="h-24 bg-white border border-gray-200 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  if (error || !hotel) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-gradient-to-br from-blue-50 via-white to-teal-50">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden max-w-md w-full">
          <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-6 py-3">
            <h3 className="text-white font-bold">Hotel Unavailable</h3>
          </div>
          <div className="p-6 text-center">
            <AlertTriangle className="w-10 h-10 text-red-500 mb-3 mx-auto" />
            <p className="text-red-600 font-bold mb-3" data-testid="hotel-detail-error">{error || 'Hotel not found.'}</p>
            <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-lg text-sm font-bold" data-testid="hotel-detail-back">Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  const images = hotel.images || [];
  const fullAddress = _fmtAddress(hotel.address);
  const cityName = hotel.address?.city?.name || hotel.address?.ctn || '';
  const propertyType = _propertyTypeLabel(hotel.property_type);
  const desc = hotel.description || {};
  const overview = desc.headline || desc.overview || (typeof hotel.description === 'string' ? hotel.description : '');
  const geo = hotel.geo;
  const mapUrl = (geo?.lt && geo?.ln) ? `https://www.google.com/maps?q=${geo.lt},${geo.ln}` : null;
  const checkIn = _fmtCheckTime(hotel.checkin_time, 'From');
  const checkOut = _fmtCheckTime(hotel.checkout_time, 'Until');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 pb-24 md:pb-12" data-testid="hotel-detail-page">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">

        {/* ── Back + Auth chip (mirrors flight booking) ── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all shadow-sm hover:shadow-md"
            data-testid="hotel-detail-back"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Results
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSaved((s) => !s)}
              className={`p-2 bg-white border border-gray-200 rounded-xl transition-all shadow-sm hover:shadow-md ${saved ? 'text-red-500' : 'text-gray-600'}`}
              data-testid="hotel-detail-save"
              aria-label="Save"
            >
              <Heart className={`w-4 h-4 ${saved ? 'fill-red-500' : ''}`} />
            </button>
            <button
              type="button"
              onClick={() => navigator.share?.({ title: hotel.name, url: window.location.href }).catch(() => {})}
              className="p-2 bg-white border border-gray-200 rounded-xl text-gray-600 transition-all shadow-sm hover:shadow-md"
              data-testid="hotel-detail-share"
              aria-label="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
            {isAgent && agent ? (
              <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Agent</p>
                    <p className="text-sm font-bold text-gray-800">{agent.full_name}</p>
                  </div>
                </div>
                <button onClick={agentLogout} className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1 ml-2">
                  <LogOut className="w-3 h-3" /> Logout
                </button>
              </div>
            ) : isB2C && b2cUser ? (
              <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#1B375B] to-[#659A9B] flex items-center justify-center">
                  <UserIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{b2cUser.full_name}</p>
                  <p className="text-xs text-gray-500">{b2cUser.email}</p>
                </div>
                <button onClick={b2cLogout} className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1">
                  <LogOut className="w-3 h-3" /> Logout
                </button>
              </div>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                <LogIn className="w-4 h-4" /> Login
              </button>
            )}
          </div>
        </div>

        <div className="space-y-5">
        {/* ── Title block (themed section card) ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-testid="hotel-detail-title-block">
          <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-white font-bold text-base sm:text-lg truncate" data-testid="hotel-detail-name">{hotel.name}</h2>
              {propertyType && (
                <span className="px-2 py-0.5 bg-white/15 text-white text-[10px] font-extrabold rounded-full uppercase tracking-wider shrink-0" data-testid="hotel-detail-property-type">
                  {propertyType}
                </span>
              )}
            </div>
            {startingPrice && (
              <button
                type="button"
                onClick={() => roomsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="hidden md:flex items-center gap-2 px-4 py-1.5 bg-amber-400 text-[#1B375B] rounded-lg text-xs font-extrabold hover:bg-amber-300 shadow-md"
                data-testid="hotel-detail-stickybar-reserve"
              >
                From ₹{Math.round(startingPrice).toLocaleString('en-IN')} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="p-4 sm:p-5 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <_StarRow rating={hotel.rating} />
                {hotel.rating >= 4 && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-extrabold rounded-full">EXCELLENT</span>
                )}
              </div>
              {fullAddress && (
                <p className="text-xs sm:text-sm text-gray-600 flex items-start gap-1.5" data-testid="hotel-detail-full-address">
                  <MapPin className="w-4 h-4 text-[#659A9B] mt-0.5 shrink-0" />
                  <span>{fullAddress}</span>
                </p>
              )}
            </div>
            <div className="text-right shrink-0 hidden sm:block">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Starts from</p>
              <p className="text-2xl font-extrabold text-[#1B375B] leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ₹{startingPrice ? Math.round(startingPrice).toLocaleString('en-IN') : '—'}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">+ taxes & fees</p>
            </div>
          </div>
        </div>

        {/* ── Gallery hero ── */}
        {images.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-2 rounded-2xl overflow-hidden h-72 sm:h-[420px]" data-testid="hotel-detail-gallery">
            <button
              type="button"
              onClick={() => setLightboxIdx(0)}
              className="relative group bg-gray-200 overflow-hidden"
            >
              <img src={images[0]} alt={hotel.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
            <div className="hidden sm:grid grid-cols-2 grid-rows-2 gap-2">
              {[1, 2, 3, 4].map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setLightboxIdx(i)}
                  className="relative group bg-gray-200 overflow-hidden disabled:cursor-default"
                  disabled={!images[i]}
                >
                  {images[i] ? (
                    <>
                      <img src={images[i]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      {i === 4 && images.length > 5 && (
                        <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white font-extrabold text-sm gap-1.5 group-hover:bg-black/65 transition-colors">
                          <Camera className="w-4 h-4" /> +{images.length - 5} more
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1B375B]/10 to-[#659A9B]/15 flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-[#1B375B]/30" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Quick-facts strip ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="hotel-detail-quickfacts">
          {checkIn && (
            <div className="bg-white rounded-xl p-3 shadow-sm flex items-start gap-2">
              <Clock className="w-4 h-4 text-[#659A9B] mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Check-in</p>
                <p className="text-xs font-extrabold text-[#1B375B]">{checkIn.replace('From ', '')}</p>
              </div>
            </div>
          )}
          {checkOut && (
            <div className="bg-white rounded-xl p-3 shadow-sm flex items-start gap-2">
              <Clock className="w-4 h-4 text-[#659A9B] mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Check-out</p>
                <p className="text-xs font-extrabold text-[#1B375B]">{checkOut.replace('Until ', '')}</p>
              </div>
            </div>
          )}
          {safeOptions.some((o) => o.is_refundable) && (
            <div className="bg-emerald-50 rounded-xl p-3 shadow-sm flex items-start gap-2 border border-emerald-100" data-testid="hotel-detail-fact-refundable">
              <BadgeCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-600">Cancellation</p>
                <p className="text-xs font-extrabold text-emerald-700">Free cancel available</p>
              </div>
            </div>
          )}
          {amenityRows.some((r) => r.group === 'WiFi') && (
            <div className="bg-white rounded-xl p-3 shadow-sm flex items-start gap-2">
              <Wifi className="w-4 h-4 text-[#659A9B] mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Internet</p>
                <p className="text-xs font-extrabold text-[#1B375B]">Free WiFi</p>
              </div>
            </div>
          )}
        </div>

        {/* ── 2-col content: About + Amenities + Map | Sticky info card ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 sm:gap-5">
          <div className="space-y-4 min-w-0">
            {(overview || desc.rooms || desc.location || desc.dining || desc.attractions) && (
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="hotel-detail-about">
                <h3 className="text-base sm:text-lg font-extrabold text-[#1B375B] mb-2.5 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Info className="w-4 h-4 text-[#659A9B]" />
                  About this property
                </h3>
                {overview && <p className="text-sm text-gray-700 leading-relaxed mb-2 whitespace-pre-line">{overview}</p>}
                {desc.location && <p className="text-sm text-gray-700 leading-relaxed mb-2"><span className="font-bold text-[#1B375B]">Location · </span>{desc.location}</p>}
                {desc.rooms && <p className="text-sm text-gray-700 leading-relaxed mb-2"><span className="font-bold text-[#1B375B]">Rooms · </span>{desc.rooms}</p>}
                {desc.dining && <p className="text-sm text-gray-700 leading-relaxed mb-2"><span className="font-bold text-[#1B375B]">Dining · </span>{desc.dining}</p>}
                {desc.attractions && <p className="text-sm text-gray-700 leading-relaxed mb-2"><span className="font-bold text-[#1B375B]">Attractions · </span>{desc.attractions}</p>}
                {desc.business_amenities && <p className="text-sm text-gray-700 leading-relaxed"><span className="font-bold text-[#1B375B]">Business · </span>{desc.business_amenities}</p>}
              </div>
            )}

            {amenityRows.length > 0 && (
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="hotel-detail-amenities">
                <h3 className="text-base sm:text-lg font-extrabold text-[#1B375B] mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Sparkles className="w-4 h-4 text-[#659A9B]" />
                  Property amenities
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {amenityRows.map(({ name, Icon }, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700" data-testid={`hotel-detail-amenity-${i}`}>
                      <span className="w-7 h-7 rounded-lg bg-[#659A9B]/10 flex items-center justify-center shrink-0">
                        <Icon className="w-3.5 h-3.5 text-[#659A9B]" />
                      </span>
                      <span className="text-xs sm:text-sm leading-snug">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mapUrl && (
              <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm" data-testid="hotel-detail-map">
                <h3 className="text-base sm:text-lg font-extrabold text-[#1B375B] mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <MapPin className="w-4 h-4 text-[#659A9B]" />
                  Location
                </h3>
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative h-48 sm:h-56 rounded-xl overflow-hidden bg-gradient-to-br from-[#1B375B]/10 to-[#659A9B]/10 group"
                  data-testid="hotel-detail-map-link"
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <MapPin className="w-10 h-10 text-[#1B375B]/40 mb-2 group-hover:scale-110 transition-transform" />
                    <p className="text-xs font-bold text-[#1B375B]">{cityName}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{geo.lt}, {geo.ln}</p>
                    <span className="mt-2 text-[10px] font-extrabold text-[#659A9B] underline">Open in Google Maps →</span>
                  </div>
                </a>
                {fullAddress && <p className="text-xs text-gray-600 mt-2 leading-relaxed">{fullAddress}</p>}
              </div>
            )}
          </div>

          {/* Sticky right rail — Phase H6: morphs into "Your Selection" card when an option is picked */}
          <aside className="lg:sticky lg:top-20 lg:self-start bg-white rounded-2xl p-4 shadow-sm border border-[#659A9B]/15" data-testid="hotel-detail-stickyrail">
            {selectedOption ? (
              <div data-testid="hotel-detail-your-selection">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#659A9B]">Your Selection</p>
                  <button
                    type="button"
                    onClick={() => setSelectedOptId(null)}
                    className="text-[10px] text-gray-400 hover:text-rose-500 font-bold"
                    data-testid="hotel-detail-clear-selection"
                  >
                    Change
                  </button>
                </div>
                <p className="text-sm font-extrabold text-[#1B375B] leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {selectedOption.room_type_group_label || selectedOption.name || 'Standard Room'}
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(selectedOption.meal_basis || selectedOption.mb) && (
                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[9px] font-bold rounded">
                      {Array.isArray(selectedOption.meal_basis || selectedOption.mb)
                        ? (selectedOption.meal_basis || selectedOption.mb).join(', ')
                        : (selectedOption.meal_basis || selectedOption.mb)}
                    </span>
                  )}
                  {selectedOption.is_refundable && (
                    <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-bold rounded">Refundable</span>
                  )}
                  {selectedOption.is_non_refundable && (
                    <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 text-[9px] font-bold rounded">Non-refundable</span>
                  )}
                </div>
                {selectedOption.is_refundable && selectedOption.free_cancellation_until && (
                  <p className="text-[10px] text-emerald-700 font-semibold mt-1.5 flex items-center gap-1">
                    <BadgeCheck className="w-3 h-3" /> Free cancel till {_fmtDateBadge(selectedOption.free_cancellation_until)}
                  </p>
                )}

                {/* Phase H6.C — Per-room mini-summary in selection rail */}
                {Array.isArray(selectedOption.rooms_in_option) && selectedOption.rooms_in_option.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-gray-100" data-testid="hotel-detail-selection-rooms">
                    <p className="text-[9px] uppercase tracking-wider font-bold text-[#659A9B] mb-1.5">
                      {selectedOption.rooms_in_option.length} rooms included
                    </p>
                    <div className="space-y-1">
                      {selectedOption.rooms_in_option.map((ri, riIdx) => {
                        const _rType = ri.rt || ri.rc || `Room ${riIdx + 1}`;
                        const _adt = ri.adt || 0;
                        const _chd = ri.chd || 0;
                        return (
                          <div key={riIdx} className="flex items-start gap-1.5 text-[10px] text-gray-600 leading-snug">
                            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded text-[8px] font-extrabold bg-[#1B375B] text-white shrink-0 mt-0.5">{riIdx + 1}</span>
                            <span className="min-w-0 truncate">
                              <span className="font-bold text-[#1B375B]">{_rType}</span>
                              <span className="text-gray-500"> · {_adt}A{_chd ? `+${_chd}C` : ''}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Total</p>
                  <p className="text-2xl font-extrabold text-[#1B375B] leading-none mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    ₹{Number(selectedOption.total_price ?? selectedOption.tpc ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">all-inclusive</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleBookOption(selectedOption)}
                  disabled={reviewingOpt === selectedOption.id}
                  className="w-full mt-3 py-2.5 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl text-sm font-bold hover:shadow-lg disabled:opacity-60 transition-all flex items-center justify-center gap-1.5"
                  data-testid="hotel-detail-your-selection-reserve"
                >
                  {reviewingOpt === selectedOption.id
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Validating…</>
                    : <>Reserve this room <ChevronRight className="w-3.5 h-3.5" /></>}
                </button>
              </div>
            ) : (
              <>
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Starting from</p>
                <p className="text-3xl font-extrabold text-[#1B375B] leading-none mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ₹{startingPrice ? Math.round(startingPrice).toLocaleString('en-IN') : '—'}
                </p>
                <p className="text-[10px] text-gray-400 mt-1">per stay · all-inclusive</p>

                <div className="mt-3 space-y-2 text-xs text-gray-700 border-t border-gray-100 pt-3">
                  {checkIn && <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#659A9B]" /> Check-in: <span className="font-bold text-[#1B375B]">{checkIn.replace('From ', '')}</span></div>}
                  {checkOut && <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#659A9B]" /> Check-out: <span className="font-bold text-[#1B375B]">{checkOut.replace('Until ', '')}</span></div>}
                  <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[#659A9B]" /> {safeOptions.length} room option{safeOptions.length === 1 ? '' : 's'}</div>
                </div>

                <button
                  type="button"
                  onClick={() => roomsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="w-full mt-3 py-2.5 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl text-sm font-bold hover:shadow-lg transition-all flex items-center justify-center gap-1.5"
                  data-testid="hotel-detail-sticky-rooms-cta"
                >
                  See Rooms <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </aside>
        </div>

        {/* ── Rooms section (gradient header — focal point) ── */}
        <div ref={roomsRef} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden scroll-mt-20" data-testid="hotel-detail-options">
          <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4 flex items-end justify-between">
            <div>
              <h3 className="text-white font-bold text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Available Rooms
              </h3>
              <p className="text-[11px] text-white/70 mt-0.5">{safeOptions.length} option{safeOptions.length === 1 ? '' : 's'} across {groups.length} room type{groups.length === 1 ? '' : 's'}</p>
            </div>
            <p className="hidden sm:block text-[10px] text-white/70 uppercase tracking-wider font-bold">INR · inclusive of taxes</p>
          </div>

          {/* Phase H6 — Filter chips bar */}
          {safeOptions.length > 0 && (
            <div className="px-4 sm:px-5 pt-4 pb-2 border-b border-gray-100 bg-gray-50/60" data-testid="hotel-detail-filter-chips">
              <div className="flex items-center flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-500 mr-1">
                  <Filter className="w-3 h-3" /> Filter
                </span>
                {[
                  { k: 'refundable', label: 'Free cancellation', icon: ShieldCheck, count: chipCounts.refundable, activeBg: 'bg-emerald-600 text-white border-emerald-600', idleBg: 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50' },
                  { k: 'breakfast', label: 'Breakfast included', icon: Coffee, count: chipCounts.breakfast, activeBg: 'bg-amber-600 text-white border-amber-600', idleBg: 'bg-white text-amber-800 border-amber-300 hover:bg-amber-50' },
                  { k: 'noPan', label: 'No PAN required', icon: Info, count: chipCounts.noPan, activeBg: 'bg-[#1B375B] text-white border-[#1B375B]', idleBg: 'bg-white text-[#1B375B] border-[#1B375B]/30 hover:bg-[#1B375B]/5' },
                  { k: 'noPassport', label: 'No Passport', icon: Info, count: chipCounts.noPassport, activeBg: 'bg-[#1B375B] text-white border-[#1B375B]', idleBg: 'bg-white text-[#1B375B] border-[#1B375B]/30 hover:bg-[#1B375B]/5' },
                ].map(({ k, label, icon: Icon, count, activeBg, idleBg }) => {
                  const active = filters[k];
                  const disabled = count === 0;
                  return (
                    <button
                      key={k}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleFilter(k)}
                      data-testid={`hotel-detail-filter-chip-${k}`}
                      data-active={active ? '1' : '0'}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-[11px] font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${active ? activeBg : idleBg}`}
                    >
                      <Icon className="w-3 h-3" />
                      {label}
                      <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold ${active ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
                    </button>
                  );
                })}
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    data-testid="hotel-detail-filter-clear"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold text-gray-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                  >
                    <X className="w-3 h-3" /> Clear ({activeFilterCount})
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="p-4 sm:p-5 space-y-3">

          {safeOptions.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500" data-testid="hotel-detail-no-options">
              <Building2 className="w-12 h-12 text-[#1B375B]/30 mx-auto mb-2" />
              No rooms available for the selected dates.
            </div>
          )}

          {safeOptions.length > 0 && filteredOptions.length === 0 && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-6 text-center text-sm text-amber-800" data-testid="hotel-detail-no-options-after-filter">
              <AlertTriangle className="w-9 h-9 text-amber-500 mx-auto mb-2" />
              No rooms match your filters. Try removing one or two.
              <button
                type="button"
                onClick={clearFilters}
                className="block mx-auto mt-3 text-xs font-bold text-amber-900 underline hover:text-amber-700"
              >
                Clear all filters
              </button>
            </div>
          )}

          {/* Phase H6 — accordion-grouped option list (one bucket per room type) */}
          {groups.map((g) => {
            const isOpen = expandedGroups.has(g.key);
            return (
              <div key={g.key} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`hotel-detail-accordion-${g.key}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(g.key)}
                  data-testid={`hotel-detail-accordion-toggle-${g.key}`}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-9 h-9 rounded-lg bg-[#659A9B]/10 flex items-center justify-center shrink-0">
                      <Building2 className="w-4 h-4 text-[#659A9B]" />
                    </span>
                    <div className="min-w-0">
                      <h4 className="text-sm sm:text-base font-extrabold text-[#1B375B] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{g.label}</h4>
                      <div className="flex items-center flex-wrap gap-1.5 mt-0.5">
                        <span className="text-[10px] text-gray-500 font-semibold">{g.options.length} option{g.options.length === 1 ? '' : 's'}</span>
                        {g.any_refundable && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 font-bold rounded">Free cancel available</span>}
                        {g.any_breakfast && <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-800 font-bold rounded">Breakfast option</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <p className="text-[9px] text-gray-400 uppercase tracking-wider font-bold leading-none">From</p>
                      <p className="text-base font-extrabold text-[#1B375B] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        ₹{Number.isFinite(g.from_price) ? Math.round(g.from_price).toLocaleString('en-IN') : '—'}
                      </p>
                    </div>
                    {isOpen
                      ? <ChevronUp className="w-4 h-4 text-[#659A9B]" />
                      : <ChevronDown className="w-4 h-4 text-[#659A9B]" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 p-3 sm:p-4 space-y-2.5 bg-gray-50/50" data-testid={`hotel-detail-accordion-body-${g.key}`}>
                    {g.options.map((opt) => {
                      const price = opt.total_price ?? opt.tpc;
                      const meals = opt.meal_basis || opt.mb;
                      const isRefundable = !!opt.is_refundable;
                      const isNonRefundable = !!opt.is_non_refundable;
                      const freeUntil = opt.free_cancellation_until;
                      const adults = opt.adults || (opt.rooms_in_option || []).reduce((s, r) => s + (r.adt || 0), 0);
                      const children = opt.children || (opt.rooms_in_option || []).reduce((s, r) => s + (r.chd || 0), 0);
                      const isSelected = selectedOptId === opt.id;
                      return (
                        <div
                          key={opt.id}
                          className={`bg-white rounded-xl border transition-all p-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 cursor-pointer ${isSelected ? 'border-[#1B375B] ring-2 ring-[#659A9B]/40 shadow' : 'border-gray-200 hover:border-[#659A9B]/40'}`}
                          data-testid={`hotel-detail-option-${opt.id}`}
                          data-selected={isSelected ? '1' : '0'}
                          onClick={() => handleSelectOption(opt)}
                        >
                          <div className="min-w-0 space-y-2">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {(adults > 0 || children > 0) && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-[10px] font-bold rounded flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {adults} adult{adults !== 1 ? 's' : ''}{children ? `, ${children} child` : ''}
                                </span>
                              )}
                              {meals && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded flex items-center gap-1">
                                  <Coffee className="w-3 h-3" /> {Array.isArray(meals) ? meals.join(', ') : meals}
                                </span>
                              )}
                              {isRefundable && (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded flex items-center gap-1" data-testid={`hotel-option-refundable-${opt.id}`}>
                                  <ShieldCheck className="w-3 h-3" /> Refundable
                                </span>
                              )}
                              {isNonRefundable && (
                                <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded">Non-refundable</span>
                              )}
                              {opt.pan_required && (
                                <span className="px-2 py-0.5 bg-rose-50 text-rose-700 text-[10px] font-bold rounded border border-rose-200" title="PAN card required">PAN required</span>
                              )}
                              {opt.passport_required && (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded border border-blue-200" title="Passport required">Passport required</span>
                              )}
                            </div>

                            {isRefundable && freeUntil && (
                              <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1" data-testid={`hotel-option-free-until-${opt.id}`}>
                                <BadgeCheck className="w-3 h-3" />
                                Free cancellation until {_fmtDateBadge(freeUntil)}
                              </p>
                            )}

                            {(opt.room_facilities || []).length > 0 && (
                              <p className="text-[11px] text-gray-500">
                                <span className="font-bold text-gray-600">Includes:</span> {opt.room_facilities.slice(0, 4).join(' · ')}
                              </p>
                            )}

                            {/* Phase H6.C — Per-room breakdown when option covers >1 room.
                                Tripjack bundles all searched rooms into one option;
                                showing them upfront removes user confusion. */}
                            {Array.isArray(opt.rooms_in_option) && opt.rooms_in_option.length > 1 && (
                              <div className="pt-2 mt-1 border-t border-dashed border-gray-200" data-testid={`hotel-option-rooms-${opt.id}`}>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-[#659A9B] mb-1.5">
                                  {opt.rooms_in_option.length} rooms in this booking
                                </p>
                                <div className="space-y-1">
                                  {opt.rooms_in_option.map((ri, riIdx) => {
                                    const _rType = ri.rt || ri.rc || `Room ${riIdx + 1}`;
                                    const _adt = ri.adt || 0;
                                    const _chd = ri.chd || 0;
                                    const _mb = ri.mb;
                                    const _bt = ri.bt || ri.bedType;
                                    return (
                                      <div key={riIdx} className="flex items-start gap-2 text-[11px] text-gray-600 leading-snug" data-testid={`hotel-option-room-${opt.id}-${riIdx}`}>
                                        <span className="inline-flex items-center justify-center w-4 h-4 rounded text-[8px] font-extrabold bg-[#1B375B] text-white shrink-0 mt-0.5">{riIdx + 1}</span>
                                        <span className="min-w-0">
                                          <span className="font-bold text-[#1B375B]">{_rType}</span>
                                          {_bt && <span className="text-gray-500"> · {_bt}</span>}
                                          <span className="text-gray-500"> · {_adt} adult{_adt !== 1 ? 's' : ''}{_chd ? `, ${_chd} child${_chd !== 1 ? 'ren' : ''}` : ''}</span>
                                          {_mb && _mb !== meals && <span className="text-amber-700"> · {Array.isArray(_mb) ? _mb.join(', ') : _mb}</span>}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="sm:text-right sm:min-w-[170px] flex sm:flex-col items-end justify-between sm:justify-center gap-2 border-t sm:border-t-0 sm:border-l border-gray-100 pt-3 sm:pt-0 sm:pl-4">
                            <div className="text-right">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Total</p>
                              <p className="text-xl font-extrabold text-[#1B375B] leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                ₹{Number(price ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </p>
                              <p className="text-[10px] text-gray-400 mt-0.5">incl. taxes</p>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleBookOption(opt); }}
                              disabled={reviewingOpt === opt.id}
                              data-testid={`hotel-detail-book-${opt.id}`}
                              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl text-xs font-extrabold hover:shadow-lg disabled:opacity-60 transition-all shadow-md"
                            >
                              {reviewingOpt === opt.id
                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Validating…</>
                                : <>Reserve <ChevronRight className="w-3.5 h-3.5" /></>}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {error && <p className="text-xs text-red-500 font-semibold" data-testid="hotel-detail-inline-error">{error}</p>}
          </div>
        </div>

        {/* ── Policies (themed card) ── */}
        {(checkIn || checkOut || safeOptions.some((o) => o.pan_required || o.passport_required)) && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-testid="hotel-detail-policies">
            <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4 flex items-center gap-2">
              <Info className="w-4 h-4 text-white" />
              <h3 className="text-white font-bold">Hotel policies</h3>
            </div>
            <div className="p-5">
              <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                {checkIn && <li className="flex items-start gap-2"><Clock className="w-3.5 h-3.5 text-[#659A9B] mt-0.5 shrink-0" /><span><strong>Check-in:</strong> {checkIn}</span></li>}
                {checkOut && <li className="flex items-start gap-2"><Clock className="w-3.5 h-3.5 text-[#659A9B] mt-0.5 shrink-0" /><span><strong>Check-out:</strong> {checkOut}</span></li>}
                {safeOptions.some((o) => o.pan_required) && (
                  <li className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-rose-500 mt-0.5 shrink-0" /><span><strong>PAN card</strong> required at check-in for select rooms (Indian guests).</span></li>
                )}
                {safeOptions.some((o) => o.passport_required) && (
                  <li className="flex items-start gap-2"><AlertTriangle className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" /><span><strong>Passport</strong> required at check-in for select rooms (foreign guests).</span></li>
                )}
              </ul>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Mobile sticky CTA */}
      {startingPrice && (
        <div className="md:hidden fixed bottom-16 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-2xl px-3 py-2.5 flex items-center justify-between gap-3" data-testid="hotel-detail-mobile-cta">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Starts from</p>
            <p className="text-lg font-extrabold text-[#1B375B] leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ₹{Math.round(startingPrice).toLocaleString('en-IN')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => roomsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl text-sm font-extrabold hover:shadow-lg transition-all"
            data-testid="hotel-detail-mobile-reserve"
          >
            See Rooms <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {lightboxIdx !== null && <Lightbox images={images} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />}

      <Footer />
      <BottomNav />
    </div>
  );
};

export default HotelDetailPage;
